import { environment } from "../../config/environment";
import * as XLSX from "xlsx";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../Sidebar";
import RevOpsLineGraph from "./RevOpsLineGraph";
import RevOpsPie from "./RevOpsPie";
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import {
  RevOpsCustomerDoctorSearchbar,
  RevOpsSpecialtyDoctorSearchbar,
} from "./RevOpsDoctorSearchbar";
import { Card, CardContent, CardDescription } from "../ui/Card";
import {
  getActiveSuperOrgsCount,
  getActiveSuperOrgsList,
  getActiveVsTotal,
  getAllRevOpsCustomersLegend,
  getDailyActivations,
  getActiveUsageDetails,
  getDoctorsByMonth,
  getPatientsSeenThisMonth,
  getAllRevOpsSpecialties,
  getNewDoctorsThisMonth,
  getRevOpsSuperOrgDoctors,
  queryDoctorsWithProcessingNotes,
  queryDoctorsByRevOpsSpecialty,
  queryNotesProcessing,
  renderSixMonthSpan,
} from "../../utils/functions";
import { UserStar, Users, UserRoundPlus, TrendingUp, Hospital, CalendarSearch, Mic, CheckCheck } from "lucide-react";
import { customFetch } from "../../utils/Auth";

const SectionHeader = ({ title, meta, size }) => (
  <div className="flex items-center justify-between">
    <h3 className={`${size || "text-base"} font-semibold text-slate-800 uppercase tracking-wide`}>{title}</h3>
    {meta && <span className="text-xs uppercase tracking-wide text-slate-500">{meta}</span>}
  </div>
);

const AdoptionDoctorsMiniTable = ({ title, doctors, loading, emptyLabel }) => (
  <div>
    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</div>
    <div className="max-h-[260px] overflow-y-auto overflow-x-auto border border-slate-200 rounded-lg">
      <table className="min-w-full text-sm text-slate-700">
        <thead className="bg-slate-50 sticky top-0">
          <tr>
            <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Name</th>
            <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Email</th>
            <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Specialty</th>
            <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Customer</th>
            <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Super Org</th>
            <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Active Months</th>
            <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Recordings in Period</th>
            <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Created At</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={8} className="px-3 py-6 text-center text-slate-400">
                Loading...
              </td>
            </tr>
          ) : doctors.length === 0 ? (
            <tr>
              <td colSpan={8} className="px-3 py-6 text-center text-slate-400">
                {emptyLabel}
              </td>
            </tr>
          ) : (
            doctors.map((doctor, index) => (
              <tr key={`${doctor.email}-${index}`} className="border-b border-slate-100">
                <td className="px-3 py-2">{doctor.name}</td>
                <td className="px-3 py-2">{doctor.email}</td>
                <td className="px-3 py-2">{doctor.specialty}</td>
                <td className="px-3 py-2">{doctor.superOrg}</td>
                <td className="px-3 py-2">{doctor.doctorSuperOrg}</td>
                <td className="px-3 py-2">{doctor.activeMonths}</td>
                <td className="px-3 py-2">{doctor.numberOfRecordings}</td>
                <td className="px-3 py-2">{doctor.createdAt}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const formatNumber = (value) => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  return typeof num === "number" && !isNaN(num)
    ? num.toLocaleString("en-US")
    : value ?? "—";
};

const formatCurrency = (value) =>
  typeof value === "number"
    ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : value ?? "—";

const formatDateLabel = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const formatMonthLabel = (value) => {
  if (!value) return "—";
  const parsed = new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", { year: "numeric", month: "short" });
};

const normalizeAdoptionMonthDoctors = (doctors) =>
  (doctors || [])
    .map((doctor) => {
      const numberOfRecordings = typeof doctor?.number_of_recordings === "number"
        ? doctor.number_of_recordings
        : Number(doctor?.number_of_recordings || doctor?.numberOfRecordings || 0);
      const activeMonths = Array.isArray(doctor?.active_months) ? doctor.active_months : [];

      return {
        name: doctor?.user_name || doctor?.userName || doctor?.name || "—",
        email: doctor?.user_email || doctor?.userEmail || doctor?.email || "—",
        organization: doctor?.organization || "—",
        specialty: doctor?.specialty || "—",
        superOrg: doctor?.super_org || doctor?.superOrg || "—",
        doctorSuperOrg: doctor?.doctor_super_org || doctor?.doctorSuperOrg || "—",
        createdAt: formatDateLabel(doctor?.created_at || doctor?.createdAt),
        recentNote: formatDateLabel(doctor?.recent_note || doctor?.recentNote),
        activeMonths: activeMonths.map(formatMonthLabel).join(", "),
        numberOfRecordings,
      };
    })
    .sort((a, b) => b.numberOfRecordings - a.numberOfRecordings);

// "YYYY-MM" -> "YYYY-MM" inclusive list, ascending. Returns [] if the range is invalid/reversed.
const getMonthsInRange = (fromMonth, toMonth) => {
  if (!/^\d{4}-\d{2}$/.test(fromMonth || "") || !/^\d{4}-\d{2}$/.test(toMonth || "")) return [];
  const [fromYear, fromMon] = fromMonth.split("-").map(Number);
  const [toYear, toMon] = toMonth.split("-").map(Number);
  if (fromYear > toYear || (fromYear === toYear && fromMon > toMon)) return [];

  const months = [];
  let year = fromYear;
  let mon = fromMon;
  // hard cap so a bad input can't spin this forever
  for (let i = 0; i < 240; i += 1) {
    months.push(`${year}-${String(mon).padStart(2, "0")}`);
    if (year === toYear && mon === toMon) break;
    mon += 1;
    if (mon > 12) {
      mon = 1;
      year += 1;
    }
  }
  return months;
};

const ADOPTION_DOCTORS_CSV_HEADERS = [
  "Name",
  "Email",
  "Specialty",
  "Customer",
  "Super Org",
  "Active Months",
  "Recordings in Period",
  "Created At",
];

// Shared by both the single-month download and the month-range export so the two
// XLSX outputs always have identical columns.
const buildAdoptionDoctorsSheetRows = (doctors) =>
  doctors.map((doctor) => ({
    Name: doctor.name,
    Email: doctor.email,
    Specialty: doctor.specialty,
    Customer: doctor.superOrg,
    "Super Org": doctor.doctorSuperOrg,
    "Active Months": doctor.activeMonths,
    "Recordings in Period": doctor.numberOfRecordings,
    "Created At": doctor.createdAt,
  }));

const triggerAdoptionDoctorsXlsxDownload = (doctors, sheetName, filename) => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(buildAdoptionDoctorsSheetRows(doctors), {
    header: ADOPTION_DOCTORS_CSV_HEADERS,
  });
  // Excel sheet names: max 31 chars, no \ / ? * [ ] :
  const safeName = String(sheetName || "Export").replace(/[\\/?*[\]:]/g, "-").slice(0, 31);
  XLSX.utils.book_append_sheet(workbook, worksheet, safeName);
  XLSX.writeFile(workbook, filename);
};

const parseMoney = (value) => {
  if (value === null || value === undefined) return null;
  const raw = value.toString().trim();
  if (!raw) return null;
  const negative = raw.startsWith("(") && raw.endsWith(")");
  const cleaned = raw.replace(/[$,()]/g, "");
  const num = Number(cleaned);
  if (Number.isNaN(num)) return null;
  return negative ? -num : num;
};

const normalizeName = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .replace(/[\s\u00a0]+/g, " ")
    .replace(/[.,()]/g, "")
    .trim();

const getAmountColumnIndex = (headers = []) => {
  if (!Array.isArray(headers)) return -1;
  const matchIndex = headers.findIndex((header) => {
    const label = (header || "").toString().toLowerCase();
    return label.includes("total") || label.includes("amount") || label.includes("sales");
  });
  return matchIndex;
};

const SlantedBar = ({ x, y, width, height, fill }) => {
  const depth = Math.max(4, Math.min(10, Math.round(width * 0.2)));
  const cap = Math.max(4, Math.min(10, Math.round(height * 0.12)));
  const frontX = x;
  const frontY = y + cap;
  const frontWidth = width - depth;
  const frontHeight = height - cap;

  if (frontWidth <= 0 || frontHeight <= 0) {
    return <rect x={x} y={y} width={width} height={height} fill={fill} />;
  }

  const topPoints = [
    `${frontX},${frontY}`,
    `${frontX + frontWidth},${frontY}`,
    `${frontX + frontWidth + depth},${frontY - cap}`,
    `${frontX + depth},${frontY - cap}`,
  ].join(" ");

  const sidePoints = [
    `${frontX + frontWidth},${frontY}`,
    `${frontX + frontWidth},${frontY + frontHeight}`,
    `${frontX + frontWidth + depth},${frontY + frontHeight - cap}`,
    `${frontX + frontWidth + depth},${frontY - cap}`,
  ].join(" ");

  return (
    <g>
      <polygon points={topPoints} fill="#fbe0a8" />
      <polygon points={sidePoints} fill="#d97706" opacity={0.85} />
      <rect x={frontX} y={frontY} width={frontWidth} height={frontHeight} fill={fill} />
    </g>
  );
};

const findSalesByCustomerHeader = (rows = []) => {
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (!Array.isArray(row)) continue;
    const first = (row[0] || "").toString().toLowerCase();
    const hasCustomer = first === "customer" || row.some((cell) => (cell || "").toString().toLowerCase() === "customer");
    const hasTotal = row.some((cell) => (cell || "").toString().toLowerCase() === "total");
    if (hasCustomer && hasTotal) {
      return { headerIndex: i, headerRow: row };
    }
  }
  return { headerIndex: -1, headerRow: [] };
};

const findSalesByCustomerTableMeta = (rows = [], headers = []) => {
  const { headerIndex, headerRow } = findSalesByCustomerHeader(rows);
  if (headerIndex >= 0) {
    return {
      headerIndex,
      headerRow,
      dataStartIndex: headerIndex + 1,
      amountColumnIndex: getAmountColumnIndex(headerRow),
    };
  }

  // QuickBooks sometimes uses a blank first header cell and "Total" in another column.
  const fallbackHeaderIndex = rows.findIndex((row) => {
    if (!Array.isArray(row) || row.length < 2) return false;
    const first = (row[0] || "").toString().trim().toLowerCase();
    const hasTotal = row.some((cell) => (cell || "").toString().trim().toLowerCase() === "total");
    return !first && hasTotal;
  });

  if (fallbackHeaderIndex >= 0) {
    const fallbackHeaderRow = rows[fallbackHeaderIndex] || [];
    return {
      headerIndex: fallbackHeaderIndex,
      headerRow: fallbackHeaderRow,
      dataStartIndex: fallbackHeaderIndex + 1,
      amountColumnIndex: getAmountColumnIndex(fallbackHeaderRow),
    };
  }

  const amountFromHeaders = getAmountColumnIndex(headers);
  if (amountFromHeaders >= 0) {
    return {
      headerIndex: -1,
      headerRow: [],
      dataStartIndex: 0,
      amountColumnIndex: amountFromHeaders,
    };
  }

  // Last fallback: infer the most money-like column from the data body.
  let bestColumnIndex = -1;
  let bestScore = 0;
  const maxColumns = rows.reduce((max, row) => (Array.isArray(row) ? Math.max(max, row.length) : max), 0);
  for (let col = 1; col < maxColumns; col += 1) {
    let score = 0;
    rows.forEach((row) => {
      if (!Array.isArray(row)) return;
      const firstCell = (row[0] || "").toString().toLowerCase();
      if (!firstCell || firstCell.includes("total") || firstCell.includes("subtotal")) return;
      if (parseMoney(row[col]) !== null) score += 1;
    });
    if (score > bestScore) {
      bestScore = score;
      bestColumnIndex = col;
    }
  }

  return {
    headerIndex: -1,
    headerRow: [],
    dataStartIndex: 0,
    amountColumnIndex: bestColumnIndex,
  };
};

const extractCustomerNames = (rows = [], headers = []) => {
  const { dataStartIndex } = findSalesByCustomerTableMeta(rows, headers);
  const startIndex = Math.max(dataStartIndex, 0);
  return rows.slice(startIndex).reduce((acc, row) => {
    if (!Array.isArray(row)) return acc;
    const name = normalizeName(row[0]);
    if (!name) return acc;
    if (name.includes("total") || name.includes("subtotal")) return acc;
    acc.add(name);
    return acc;
  }, new Set());
};

const extractCustomerAmounts = (rows = [], headers = []) => {
  const { dataStartIndex, amountColumnIndex } = findSalesByCustomerTableMeta(rows, headers);
  const startIndex = Math.max(dataStartIndex, 0);
  const amounts = new Map();
  rows.slice(startIndex).forEach((row) => {
    if (!Array.isArray(row)) return;
    const name = normalizeName(row[0]);
    if (!name) return;
    if (name.includes("total") || name.includes("subtotal")) return;
    if (amountColumnIndex < 0) return;
    const value = parseMoney(row[amountColumnIndex]);
    if (value === null) return;
    const rawValue = (row[amountColumnIndex] || "").toString().trim();
    amounts.set(name, rawValue || value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  });
  return amounts;
};

const isSalesMonthLabel = (label = "") => {
  const normalized = (label || "").toString().trim().toLowerCase();
  if (!normalized) return false;
  if (/^(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)\b/.test(normalized)) return true;
  if (/^\d{4}-\d{2}(-\d{2})?$/.test(normalized)) return true;
  if (/^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/.test(normalized)) return true;
  if (/\b20\d{2}\b/.test(normalized)) return true;
  return false;
};

const parseSalesByMonthSeries = (rows = [], headers = []) => {
  const sourceRows = Array.isArray(headers) && headers.length ? [headers, ...(Array.isArray(rows) ? rows : [])] : rows;
  if (!Array.isArray(sourceRows) || sourceRows.length === 0) return [];

  const getCustomerColumnIndex = (normalizedCells = []) => {
    const explicitIndex = normalizedCells.findIndex((cell) => cell.includes("customer"));
    if (explicitIndex >= 0) return explicitIndex;
    const monthLikeCount = normalizedCells.filter((cell) => isSalesMonthLabel(cell)).length;
    const hasTotal = normalizedCells.some((cell) => cell === "total");
    if (monthLikeCount >= 2 && hasTotal) {
      // QuickBooks exports often leave the first header cell blank instead of "Customer".
      return 0;
    }
    return -1;
  };

  const headerIndex = sourceRows.findIndex((row) => {
    if (!Array.isArray(row)) return false;
    const normalizedCells = row.map((cell) => (cell || "").toString().trim().toLowerCase());
    const customerIndex = getCustomerColumnIndex(normalizedCells);
    if (customerIndex < 0) return false;
    const nonCustomerCells = normalizedCells.filter((_, index) => index !== customerIndex);
    const monthLikeCount = nonCustomerCells.filter((cell) => isSalesMonthLabel(cell)).length;
    const hasTotal = nonCustomerCells.some((cell) => cell === "total");
    return monthLikeCount > 0 || hasTotal;
  });

  if (headerIndex < 0) return [];
  const headerRow = sourceRows[headerIndex];
  const normalizedHeader = headerRow.map((cell) => (cell || "").toString().trim().toLowerCase());
  const customerIndex = getCustomerColumnIndex(normalizedHeader);
  if (customerIndex < 0) return [];
  const totalIndex = normalizedHeader.findIndex(
    (cell) => cell === "total"
  );
  const monthColumns = headerRow
    .map((cell, index) => ({ label: (cell || "").toString().trim(), index }))
    .filter(({ label, index }) => {
      if (index === customerIndex || index === totalIndex) return false;
      if (!label) return false;
      if (isSalesMonthLabel(label)) return true;
      if (!isNaN(Date.parse(label))) return true;
      return false;
    });

  // Fallback for exports with unlabeled/odd month headers: use all non-total, non-customer columns.
  if (!monthColumns.length) {
    headerRow.forEach((cell, index) => {
      const label = (cell || "").toString().trim();
      if (!label) return;
      if (index === customerIndex || index === totalIndex) return;
      monthColumns.push({ label, index });
    });
  }
  if (!monthColumns.length) return [];

  const series = monthColumns.map(({ label }) => ({ name: label, total: 0 }));
  const dataStartIndex = headerIndex + 1;

  for (let i = dataStartIndex; i < sourceRows.length; i += 1) {
    const row = sourceRows[i];
    if (!Array.isArray(row)) continue;
    const firstCell = (row[customerIndex] || "").toString().toLowerCase();
    if (!firstCell || firstCell.includes("total") || firstCell.includes("subtotal")) continue;
    monthColumns.forEach(({ index }, columnIndex) => {
      const value = parseMoney(row[index]);
      if (value !== null) series[columnIndex].total += value;
    });
  }

  return series;
};

const RevOpsActivityStats = ({ data }) => {
  const stats = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return null;
    const latest = data[data.length - 1];
    const latestRate = latest.total > 0 ? (latest.active / latest.total) * 100 : 0;
    const rateSum = data.reduce((sum, entry) => {
      if (!entry.total) return sum;
      return sum + (entry.active / entry.total) * 100;
    }, 0);
    const rateCount = data.filter((entry) => entry.total).length;
    const average = rateCount ? rateSum / rateCount : null;

    return { average, latestRate };
  }, [data]);

  if (!stats || stats.average === null) return null;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Average Active Rate:</span>
        <span className="text-sm font-semibold text-gray-700">
          {stats.average.toFixed(1)}%
        </span>
      </div>
    </div>
  );
};

const ActiveDoctorsCard = ({ value, superOrgsCount, loading, onOpenGraph, tooltip }) => (
  <Card
    className="border-slate-200/80 bg-white shadow-sm rounded-xl h-full transition-shadow transition-colors cursor-pointer hover:border-ktx-purple/40 hover:shadow-md hover:bg-ktx-purple/5"
    title={tooltip}
  >
    <button
      type="button"
      onClick={onOpenGraph}
      className="w-full h-full text-left"
    >
      <CardContent className="p-5 h-full flex flex-col">
        <div className="flex items-center justify-between text-slate-500">
          <span className="flex justify-center gap-3 text-[13px] font-medium text-slate-700 uppercase tracking-wide">
            <Users size={18} className="text-[20px] text-slate-500" />
            Active Doctors
          </span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-1">
          <div className="text-center">
            <span className="inline-block text-2xl font-semibold text-ktx-purple underline underline-offset-4 tabular-nums leading-none mt-4">
              {loading ? "…" : formatNumber(value)}
            </span>
          </div>
          <span className="text-[11px] text-slate-500 mt-2">
            {`THIS MONTH`}
          </span>
        </div>
      </CardContent>
    </button>
  </Card>
);

const SmallMetricCard = ({
  icon: Icon,
  iconSize = 16,
  label,
  labelSize = 11,
  labelClassName,
  iconClassName,
  value,
  valueClassName,
  subLabel,
  subLabelClassName,
  onClick,
  cardClassName,
  mockData = false,
  tooltip,
}) => (
  <Card
    className={`border-slate-200/80 bg-white shadow-sm rounded-xl h-full transition-shadow transition-colors hover:border-ktx-purple/40 hover:shadow-md hover:bg-ktx-purple/5 ${
      onClick ? "cursor-pointer" : "cursor-default"
    } ${cardClassName || ""}`}
    title={tooltip}
  >
    <CardContent className="p-5 h-full flex flex-col relative overflow-hidden">
      {mockData && <span className="absolute inset-0 z-10 bg-white/30 pointer-events-none" />}
      {mockData && (
        <span className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center text-5xl font-semibold tracking-[0.35em] text-slate-400/70">
          MOCK
        </span>
      )}
        <div className="flex items-center justify-between text-slate-500 relative z-20">
        <span
          className={labelClassName || "flex items-center gap-2 font-medium text-slate-700 uppercase tracking-wide"}
          style={labelClassName ? undefined : { fontSize: labelSize }}
        >
          {Icon && <Icon size={iconSize} className={iconClassName || "text-slate-500"} />}
          {label}
        </span>
      </div>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="flex-1 w-full flex items-center justify-center relative z-20"
        >
          <div className="flex flex-col items-center gap-2">
            <div className="text-center">
              <span
                className={`${valueClassName || "text-2xl font-semibold text-slate-900 tabular-nums leading-none text-center mt-2"} text-ktx-purple underline underline-offset-4 inline-block`}
              >
                {value}
              </span>
            </div>
            {subLabel && (
              <div className={subLabelClassName || "text-[11px] text-slate-500 uppercase tracking-wide text-center"}>
                {subLabel}
              </div>
            )}
          </div>
        </button>
      ) : (
        <div className="flex-1 flex items-center justify-center relative z-20">
          <div className="flex flex-col items-center gap-2">
            <div className={valueClassName || "text-2xl font-semibold text-slate-900 tabular-nums leading-none text-center mt-2"}>
              {value}
            </div>
            {subLabel && (
              <div className={subLabelClassName || "text-[11px] text-slate-500 uppercase tracking-wide text-center"}>
                {subLabel}
              </div>
            )}
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

const MockCard = ({ mockData = false, className = "", children, ...props }) => (
  <Card className={`${className} bg-white relative overflow-hidden`} {...props}>
    {mockData && (
      <>
        <span className="absolute inset-0 z-10 backdrop-blur-[2px] bg-white/30" />
        <span className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center text-7xl font-semibold tracking-[0.45em] text-slate-400/70">
          MOCK
        </span>
      </>
    )}
    <div className={mockData ? "relative z-20" : undefined}>
      {children}
    </div>
  </Card>
);

const NewUsersCard = ({ value, loading, details, isOpen, onToggle, onClose, tooltip }) => (
  <Card
    className="border-slate-200/80 bg-white shadow-sm rounded-xl h-full transition-shadow transition-colors cursor-pointer hover:border-ktx-purple/40 hover:shadow-md hover:bg-ktx-purple/5"
    title={tooltip}
  >
    <CardContent className="p-5 h-full">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left h-full flex flex-col"
      >
        <div className="flex items-center justify-between text-slate-500">
          <span className="flex justify-center gap-3 text-[13px] font-medium text-slate-700 uppercase tracking-wide">
            <UserRoundPlus size={18} className="text-slate-500" />
            New Users
          </span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <div className="text-center">
            <span className="inline-block text-2xl font-semibold text-ktx-purple underline underline-offset-4 tabular-nums leading-none mt-4">
              {loading ? "…" : value}
            </span>
          </div>
          <span className="text-[11px] text-slate-500 uppercase tracking-wide text-center">
            This Month
          </span>
        </div>
      </button>
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-6">
          <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">New Users</div>
                <div className="text-xs text-slate-500">{value} total</div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Close new users"
              >
                ✕
              </button>
            </div>
            <div className="max-h-[60vh] overflow-auto p-4 text-xs text-slate-600">
              {loading ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-slate-500" />
                  Loading new users…
                </div>
              ) : details?.length ? (
                <div className="space-y-3">
                  {details.map((doctor) => (
                    <div key={doctor.user_id} className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-slate-800 font-medium">{doctor.user_name}</div>
                        <div className="truncate text-slate-500">{doctor.user_email}</div>
                      </div>
                      <div className="text-right whitespace-nowrap text-slate-400">
                        {doctor.created_at ? new Date(doctor.created_at).toLocaleDateString("en-US") : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-slate-400">No new users yet.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

const getFixedRevenueMonths = () => {
  const now = new Date();
  const year = now.getFullYear();
  const months = [6, 7, 8, 9, 10, 11]; // Jul-Dec
  return months.map((monthIndex) => {
    const date = new Date(year, monthIndex, 1);
    return {
      value: date.toISOString().slice(0, 7),
      label: date.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    };
  });
};

const buildRevenueSeries = (months) =>
  months.map((month, index) => ({
    name: month.label,
    pipeline: 220000 + index * 15000,
    arr: 120000 + index * 8000,
    collected: 100000 + index * 7000,
  }));

const useMetric = (asyncFunction, deps = []) => {
  const [value, setValue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const result = await asyncFunction();
        if (mounted) setValue(result);
      } catch (err) {
        console.error("Error loading metric:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  return { value, loading };
};

const DashboardHeader = ({ title, date, onToggleQuickLinks, showQuickLinks }) => (
  <div className="flex items-center justify-between">
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-semibold text-slate-900 mt-5">{title}</h1>
        <span className="text-xl text-slate-500">{date}</span>
      </div>
  </div>
);

export default function RevenueOperations({ setAuth, userOrg, setUserOrg }) {
  const username = localStorage.getItem("username");
  const todaysDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const activeDoctorTimeline = renderSixMonthSpan();
  const [showQuickLinks, setShowQuickLinks] = useState(false);
  const [activeVsTotal, setActiveVsTotal] = useState(null);
  const [dailyActivations, setDailyActivations] = useState(null);
  const [newUsersOpen, setNewUsersOpen] = useState(false);
  const [newUsersDetails, setNewUsersDetails] = useState(null);
  const [newUsersDetailsLoading, setNewUsersDetailsLoading] = useState(false);
  const [activityGraphOpen, setActivityGraphOpen] = useState(false);
  const [doctorAdoptionOpen, setDoctorAdoptionOpen] = useState(false);
  const [doctorAdoptionRange, setDoctorAdoptionRange] = useState("6m");
  const [selectedAdoptionMonth, setSelectedAdoptionMonth] = useState(null);
  const [adoptionMonthDoctors, setAdoptionMonthDoctors] = useState([]);
  const [adoptionMonthDoctorsLoading, setAdoptionMonthDoctorsLoading] = useState(false);
  const [adoptionMonthDoctorsTotal, setAdoptionMonthDoctorsTotal] = useState(0);
  const ADOPTION_TOP_BOTTOM_COUNT = 10;
  const [adoptionExportOpen, setAdoptionExportOpen] = useState(false);
  const [adoptionExportFromMonth, setAdoptionExportFromMonth] = useState("");
  const [adoptionExportToMonth, setAdoptionExportToMonth] = useState("");
  const [adoptionExportLoading, setAdoptionExportLoading] = useState(false);
  const [adoptionExportError, setAdoptionExportError] = useState("");
  const [adoptionExportResultCount, setAdoptionExportResultCount] = useState(null);
  // full month history for the export range pickers -- independent of whatever 3M/6M/12M/All
  // toggle the chart itself is set to, so the export range isn't limited by the chart's view.
  const [adoptionAllMonths, setAdoptionAllMonths] = useState(null);
  const [revenueBreakdownOpen, setRevenueBreakdownOpen] = useState(false);
  const [revenueBreakdownRange, setRevenueBreakdownRange] = useState("6m");
  const [openModal, setOpenModal] = useState("");
  const [revenueDatesOpen, setRevenueDatesOpen] = useState(false);
  const [revenueDatesTitle, setRevenueDatesTitle] = useState("Revenue Dates");
  const [revenueDates, setRevenueDates] = useState([]);
  const [revenueDatesType, setRevenueDatesType] = useState("sales_by_customer");
  const [agingSummaryTotal, setAgingSummaryTotal] = useState(null);
  const [agingSummaryDate, setAgingSummaryDate] = useState("");
  const [agingSummaryLoading, setAgingSummaryLoading] = useState(false);
  const [agingSummaryPeriod, setAgingSummaryPeriod] = useState("");
  const [salesByCustomerTotal, setSalesByCustomerTotal] = useState(null);
  const [salesByCustomerDate, setSalesByCustomerDate] = useState("");
  const [salesByCustomerLoading, setSalesByCustomerLoading] = useState(false);
  const [salesByCustomerPreviewTotal, setSalesByCustomerPreviewTotal] = useState(null);
  const [salesByCustomerPreviewDate, setSalesByCustomerPreviewDate] = useState("");
  const [salesByCustomerPreviewLoading, setSalesByCustomerPreviewLoading] = useState(false);
  const [salesByCustomerPeriod, setSalesByCustomerPeriod] = useState("");
  const [salesByCustomerPreviewPeriod, setSalesByCustomerPreviewPeriod] = useState("");
  const [agingSummaryCustomers, setAgingSummaryCustomers] = useState(new Set());
  const [agingSummaryCustomerAmounts, setAgingSummaryCustomerAmounts] = useState(new Map());
  const [agingSummaryCustomersLoading, setAgingSummaryCustomersLoading] = useState(false);
  const [agingSummaryPreviewTotal, setAgingSummaryPreviewTotal] = useState(null);
  const [agingSummaryPreviewDate, setAgingSummaryPreviewDate] = useState("");
  const [agingSummaryPreviewPeriod, setAgingSummaryPreviewPeriod] = useState("");
  const [agingSummaryPreviewLoading, setAgingSummaryPreviewLoading] = useState(false);
  const [selectedRevenueDate, setSelectedRevenueDate] = useState("");
  const [processingHelperData, setProcessingHelperData] = useState(null);
  const [csvKey, setCsvKey] = useState("");
  const [csvType, setCsvType] = useState("aging_summary");
  const [csvDate, setCsvDate] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvError, setCsvError] = useState("");
  const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);
  const [showRevenueBreakdownTable, setShowRevenueBreakdownTable] = useState(false);
  const [revenueBreakdownDateSelected, setRevenueBreakdownDateSelected] = useState(false);
  const [billableOrgsOpen, setBillableOrgsOpen] = useState(false);
  const [billableOrgsLoading, setBillableOrgsLoading] = useState(false);
  const [billableOrgsList, setBillableOrgsList] = useState([]);
  const [activeUsageOpen, setActiveUsageOpen] = useState(false);
  const [activeUsageLoading, setActiveUsageLoading] = useState(false);
  const [activeUsageList, setActiveUsageList] = useState([]);
  const [salesByMonthSeriesState, setSalesByMonthSeriesState] = useState([]);
  const [salesByMonthCardSeries, setSalesByMonthCardSeries] = useState([]);
  const [salesByMonthCardLoading, setSalesByMonthCardLoading] = useState(true);
  const [salesByMonthSeriesLoading, setSalesByMonthSeriesLoading] = useState(false);
  const csvCacheRef = useRef(new Map());
  const salesByMonthSummaryCacheRef = useRef(new Map());
  const salesByMonthInitRef = useRef(false);
  const specialtySearchRef = useRef(null);
  const customerSearchRef = useRef(null);
  const [selectedRevenueMonth, setSelectedRevenueMonth] = useState(() => {
    const options = getFixedRevenueMonths();
    return options.length ? options[options.length - 1].value : "";
  });

  const activeSuperOrgs = useMetric(getActiveSuperOrgsCount, []);
  const newUsers = useMetric(() => getNewDoctorsThisMonth("active"), []);
  const patients = useMetric(getPatientsSeenThisMonth, []);
  const processingStuck = useMetric(queryNotesProcessing, []);
  const revenueMonths = useMemo(() => getFixedRevenueMonths(), []);
  const revenueSeries = useMemo(() => buildRevenueSeries(revenueMonths), [revenueMonths]);
  const revenueData = useMemo(() => revenueSeries, [revenueSeries]);
  const filteredRevenueData = useMemo(() => {
    if (!Array.isArray(revenueSeries)) return revenueSeries;
    const ranges = {
      "3m": 3,
      "6m": 6,
      "12m": 12,
    };
    if (revenueBreakdownRange === "all") return revenueSeries;
    const count = ranges[revenueBreakdownRange];
    return count ? revenueSeries.slice(-count) : revenueSeries;
  }, [revenueSeries, revenueBreakdownRange]);
  const selectedRevenueLabel = useMemo(() => {
    const match = revenueMonths.find((option) => option.value === selectedRevenueMonth);
    return match ? match.label : "Prior Month";
  }, [revenueMonths, selectedRevenueMonth]);
  const selectedRevenueData = useMemo(() => {
    const match = revenueSeries.find((series) => series.name === selectedRevenueLabel);
    return match || revenueSeries[revenueSeries.length - 1] || {};
  }, [revenueSeries, selectedRevenueLabel]);
  // The "Active Doctors" card reads the same activeVsTotal data as the "Current Month
  // Activity" chart it opens (current month is always the last point, since /active-vs-total
  // always ends at the current month regardless of range) instead of calling a separate
  // /count-active-doctors endpoint. That endpoint used a different "active" definition
  // (status IN ('Locked','Review') only, no test/demo email exclusion) which could disagree
  // with what the chart showed for the same month -- deriving from the same data guarantees
  // the card and the chart can never drift apart again.
  const currentMonthActiveDoctors = useMemo(() => {
    if (!Array.isArray(activeVsTotal) || !activeVsTotal.length) return null;
    const latest = activeVsTotal[activeVsTotal.length - 1];
    return typeof latest?.active === "number" ? latest.active : Number(latest?.active) || 0;
  }, [activeVsTotal]);
  const filteredAdoptionTotals = useMemo(() => {
    if (!Array.isArray(activeVsTotal)) return activeVsTotal;
    const ranges = {
      "3m": 3,
      "6m": 6,
      "12m": 12,
    };
    if (doctorAdoptionRange === "all") return activeVsTotal;
    const count = ranges[doctorAdoptionRange];
    return count ? activeVsTotal.slice(-count) : activeVsTotal;
  }, [activeVsTotal, doctorAdoptionRange]);
  const collectedRevenueTotal = useMemo(() => {
    if (typeof salesByCustomerTotal !== "number" || typeof agingSummaryTotal !== "number") return null;
    return salesByCustomerTotal - agingSummaryTotal;
  }, [salesByCustomerTotal, agingSummaryTotal]);
  const salesByCustomerDisplayTotal = useMemo(() => {
    return typeof salesByCustomerPreviewTotal === "number"
      ? salesByCustomerPreviewTotal
      : salesByCustomerTotal;
  }, [salesByCustomerPreviewTotal, salesByCustomerTotal]);
  const salesByCustomerDisplayDate = useMemo(() => {
    return salesByCustomerPreviewDate || salesByCustomerDate || selectedRevenueDate || "";
  }, [salesByCustomerPreviewDate, salesByCustomerDate, selectedRevenueDate]);
  const salesByCustomerDisplayPeriod = useMemo(() => {
    return salesByCustomerPreviewPeriod || salesByCustomerPeriod || "";
  }, [salesByCustomerPreviewPeriod, salesByCustomerPeriod]);
  const agingSummaryDisplayTotal = useMemo(() => {
    return typeof agingSummaryPreviewTotal === "number"
      ? agingSummaryPreviewTotal
      : agingSummaryTotal;
  }, [agingSummaryPreviewTotal, agingSummaryTotal]);
  const agingSummaryDisplayDate = useMemo(() => {
    return agingSummaryPreviewDate || agingSummaryDate || "";
  }, [agingSummaryPreviewDate, agingSummaryDate]);
  const agingSummaryDisplayPeriod = useMemo(() => {
    return agingSummaryPreviewPeriod || agingSummaryPeriod || "";
  }, [agingSummaryPreviewPeriod, agingSummaryPeriod]);
  const collectedRevenueDisplayTotal = useMemo(() => {
    if (typeof salesByCustomerDisplayTotal !== "number" || typeof agingSummaryDisplayTotal !== "number") return null;
    return salesByCustomerDisplayTotal - agingSummaryDisplayTotal;
  }, [salesByCustomerDisplayTotal, agingSummaryDisplayTotal]);
  const salesByMonthSeries = useMemo(() => {
    if (csvType !== "sales_by_month") return [];
    return parseSalesByMonthSeries(csvRows, csvHeaders);
  }, [csvRows, csvHeaders, csvType]);
  const fetchSalesByMonthSeries = async (date) => {
    const summaryUrl = new URL(`${environment.apiUrl}/revop/sales-by-month-summary`);
    if (date) summaryUrl.searchParams.set("date", date);
    const summaryResponse = await customFetch(summaryUrl.toString());
    const summaryContentType = summaryResponse.headers.get("content-type");
    if (summaryContentType && summaryContentType.includes("application/json")) {
      const summaryData = await summaryResponse.json();
      if (Array.isArray(summaryData.series) && summaryData.series.length) {
        return summaryData.series;
      }
    }

    const csvUrl = new URL(`${environment.apiUrl}/revop/s3-csv`);
    csvUrl.searchParams.set("type", "sales_by_month");
    if (date) csvUrl.searchParams.set("date", date);
    csvUrl.searchParams.set("maxRows", "1000");
    const csvResponse = await customFetch(csvUrl.toString());
    const csvContentType = csvResponse.headers.get("content-type");
    if (!csvContentType || !csvContentType.includes("application/json")) {
      return [];
    }

    const csvData = await csvResponse.json();
    return parseSalesByMonthSeries(csvData.rows || [], csvData.headers || []);
  };

  const revenueBreakdownCumulative = useMemo(() => {
    if (!Array.isArray(salesByMonthSeriesState) || !salesByMonthSeriesState.length) return null;
    return salesByMonthSeriesState.reduce((sum, row) => sum + (Number(row?.total) || 0), 0);
  }, [salesByMonthSeriesState]);
  const revenueBreakdownCardCumulative = useMemo(() => {
    if (!Array.isArray(salesByMonthCardSeries) || !salesByMonthCardSeries.length) return null;
    return salesByMonthCardSeries.reduce((sum, row) => sum + (Number(row?.total) || 0), 0);
  }, [salesByMonthCardSeries]);

  useEffect(() => {
    let mounted = true;
    const loadActiveVsTotal = async () => {
      const data = await getActiveVsTotal(doctorAdoptionRange);
      if (mounted) setActiveVsTotal(data);
    };
    loadActiveVsTotal();
    return () => {
      mounted = false;
    };
  }, [doctorAdoptionRange]);

  useEffect(() => {
    // the set of selectable months changes with the range toggle, so clear any
    // month selection made under a different range instead of leaving stale picks.
    setSelectedAdoptionMonth(null);
    setAdoptionMonthDoctors([]);
    setAdoptionMonthDoctorsTotal(0);
  }, [doctorAdoptionRange]);

  useEffect(() => {
    let mounted = true;
    if (!selectedAdoptionMonth) {
      setAdoptionMonthDoctors([]);
      setAdoptionMonthDoctorsTotal(0);
      return;
    }
    const loadAdoptionMonthDoctors = async () => {
      setAdoptionMonthDoctorsLoading(true);
      try {
        // fetched once, unpaginated -- the whole list is needed anyway for the
        // top/bottom breakdown and the CSV download, and this avoids re-querying
        // the DB on every page turn like the old paginated version did.
        const { rows, totalCount } = await getDoctorsByMonth([selectedAdoptionMonth]);
        if (mounted) {
          setAdoptionMonthDoctors(normalizeAdoptionMonthDoctors(rows));
          setAdoptionMonthDoctorsTotal(totalCount);
        }
      } finally {
        if (mounted) setAdoptionMonthDoctorsLoading(false);
      }
    };
    loadAdoptionMonthDoctors();
    return () => {
      mounted = false;
    };
  }, [selectedAdoptionMonth]);

  const selectAdoptionMonth = (monthValue) => {
    setSelectedAdoptionMonth((prev) => (prev === monthValue ? null : monthValue));
  };

  const topAdoptionMonthDoctors = useMemo(
    () => adoptionMonthDoctors.slice(0, ADOPTION_TOP_BOTTOM_COUNT),
    [adoptionMonthDoctors]
  );
  const bottomAdoptionMonthDoctors = useMemo(() => {
    if (adoptionMonthDoctors.length <= ADOPTION_TOP_BOTTOM_COUNT) return [];
    const bottom = adoptionMonthDoctors.slice(-ADOPTION_TOP_BOTTOM_COUNT);
    // avoid showing the same doctor in both lists when the lists would overlap
    return bottom.filter((doctor) => !topAdoptionMonthDoctors.includes(doctor));
  }, [adoptionMonthDoctors, topAdoptionMonthDoctors]);

  const downloadAdoptionMonthXlsx = () => {
    if (!adoptionMonthDoctors.length) return;
    triggerAdoptionDoctorsXlsxDownload(
      adoptionMonthDoctors,
      selectedAdoptionMonth ? formatMonthLabel(selectedAdoptionMonth) : "Export",
      `doctor-adoption-${selectedAdoptionMonth || "export"}.xlsx`
    );
  };

  useEffect(() => {
    // lazy-load the full month history the first time the export popup opens, rather
    // than on every mount -- this is a separate "all" fetch so the export range isn't
    // limited by whatever 3M/6M/12M/All toggle the chart happens to be set to.
    if (!adoptionExportOpen || adoptionAllMonths !== null) return;
    let mounted = true;
    (async () => {
      const data = await getActiveVsTotal("all");
      if (mounted) setAdoptionAllMonths(Array.isArray(data) ? data : []);
    })();
    return () => {
      mounted = false;
    };
  }, [adoptionExportOpen, adoptionAllMonths]);

  const adoptionExportMonthOptions = useMemo(() => {
    if (!Array.isArray(adoptionAllMonths)) return [];
    return adoptionAllMonths.map((entry) => entry.name);
  }, [adoptionAllMonths]);

  useEffect(() => {
    // default the range pickers to the earliest/most recent months we have data for,
    // once that data shows up, so the popup opens with a sane range pre-filled.
    if (!adoptionExportMonthOptions.length) return;
    setAdoptionExportFromMonth((prev) => prev || adoptionExportMonthOptions[0]);
    setAdoptionExportToMonth((prev) => prev || adoptionExportMonthOptions[adoptionExportMonthOptions.length - 1]);
  }, [adoptionExportMonthOptions]);

  const downloadAdoptionRangeXlsx = async () => {
    const months = getMonthsInRange(adoptionExportFromMonth, adoptionExportToMonth);
    if (!months.length) {
      setAdoptionExportError("Pick a valid from/to month range.");
      return;
    }
    setAdoptionExportError("");
    setAdoptionExportResultCount(null);
    setAdoptionExportLoading(true);
    try {
      // A single query for the whole range, grouped by (doctor, month) on the backend,
      // rather than one HTTP round trip per month -- N separate queries was the reason a
      // multi-month range (e.g. July-August) took noticeably longer than one month did.
      // Each returned row already belongs to exactly one month (via row.month), so we
      // just bucket the one response into per-month groups here before building sheets.
      const { rows } = await getDoctorsByMonth(months);
      const rowsByMonth = new Map(months.map((month) => [month, []]));
      (rows || []).forEach((row) => {
        const month = row?.month;
        if (!rowsByMonth.has(month)) rowsByMonth.set(month, []);
        rowsByMonth.get(month).push(row);
      });
      const perMonthResults = months.map((month) => ({
        month,
        doctors: normalizeAdoptionMonthDoctors(rowsByMonth.get(month) || []),
      }));

      const workbook = XLSX.utils.book_new();
      const usedSheetNames = new Set();
      let totalDoctors = 0;

      perMonthResults.forEach(({ month, doctors }) => {
        totalDoctors += doctors.length;
        const worksheet = XLSX.utils.json_to_sheet(buildAdoptionDoctorsSheetRows(doctors), {
          header: ADOPTION_DOCTORS_CSV_HEADERS,
        });

        // Excel sheet names: max 31 chars, no \ / ? * [ ] : , and must be unique per workbook
        const baseName = formatMonthLabel(month).replace(/[\\/?*[\]:]/g, "-").slice(0, 31);
        let sheetName = baseName;
        let suffix = 2;
        while (usedSheetNames.has(sheetName)) {
          sheetName = `${baseName.slice(0, 28)} (${suffix})`;
          suffix += 1;
        }
        usedSheetNames.add(sheetName);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });

      if (!totalDoctors) {
        setAdoptionExportError("No active doctors found in that range.");
        return;
      }

      XLSX.writeFile(workbook, `doctor-adoption-${adoptionExportFromMonth}_to_${adoptionExportToMonth}.xlsx`);
      setAdoptionExportResultCount(totalDoctors);
    } catch (error) {
      setAdoptionExportError("Something went wrong generating that export.");
    } finally {
      setAdoptionExportLoading(false);
    }
  };

  useEffect(() => {
    if (csvType !== "sales_by_month") return;
    if (!salesByMonthSeries.length) return;
    setSalesByMonthSeriesState(salesByMonthSeries);
  }, [salesByMonthSeries, csvType]);

  useEffect(() => {
    let mounted = true;
    const loadDailyActivations = async () => {
      const data = await getDailyActivations();
      if (mounted) setDailyActivations(data);
    };
    loadDailyActivations();
    return () => {
      mounted = false;
    };
  }, []);

  const toggleNewUsers = async () => {
    const nextOpen = !newUsersOpen;
    setNewUsersOpen(nextOpen);
    if (nextOpen && !newUsersDetails && !newUsersDetailsLoading) {
      setNewUsersDetailsLoading(true);
      try {
        const details = await getNewDoctorsThisMonth("active", true);
        setNewUsersDetails(details || []);
      } finally {
        setNewUsersDetailsLoading(false);
      }
    }
  };

  const handleProcessingClick = async () => {
    setOpenModal("Processing");
    setProcessingHelperData(null);
    try {
      const data = await queryDoctorsWithProcessingNotes();
      setProcessingHelperData(data || []);
    } catch (err) {
      console.error("Error loading processing notes:", err);
      setProcessingHelperData([]);
    }
  };

  const loadBillableOrgs = async () => {
    setBillableOrgsLoading(true);
    try {
      const data = await getActiveSuperOrgsList();
      setBillableOrgsList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading billable orgs:", err);
      setBillableOrgsList([]);
    } finally {
      setBillableOrgsLoading(false);
    }
  };

  const openBillableOrgs = () => {
    setBillableOrgsOpen(true);
    loadBillableOrgs();
  };

  const openBillableOrgDetail = (orgName) => {
    if (!orgName) return;
    setBillableOrgsOpen(false);
    customerSearchRef.current?.submitSelection?.(orgName);
  };

  const loadActiveUsage = async () => {
    setActiveUsageLoading(true);
    try {
      const data = await getActiveUsageDetails();
      setActiveUsageList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading active usage:", err);
      setActiveUsageList([]);
    } finally {
      setActiveUsageLoading(false);
    }
  };

  const openActiveUsage = () => {
    setActiveUsageOpen(true);
    loadActiveUsage();
  };

  const openRevenueDates = (title, type) => {
    setRevenueDatesTitle(title);
    setRevenueDatesType(type);
    setRevenueDatesOpen(true);
    loadRevenueDates(type);
  };

  const openCsvPreview = () => setCsvPreviewOpen(true);

  const loadCsvPreview = async (nextKey = csvKey, nextType = csvType, nextDate = csvDate) => {
    setCsvLoading(true);
    setCsvError("");
    if (nextType !== "sales_by_month") {
      setCsvHeaders([]);
      setCsvRows([]);
    }
    const cacheKey = nextKey
      ? `key:${nextKey}`
      : `type:${nextType}|date:${nextDate}`;
    const cached = csvCacheRef.current.get(cacheKey);
    if (cached) {
      setCsvHeaders(cached.headers);
      setCsvRows(cached.rows);
      setCsvLoading(false);
      return;
    }
    try {
      const url = new URL(`${environment.apiUrl}/revop/s3-csv`);
      if (nextKey) {
        url.searchParams.set("key", nextKey);
      } else {
        url.searchParams.set("type", nextType);
        url.searchParams.set("date", nextDate);
      }
      url.searchParams.set("maxRows", "50");

      const response = await customFetch(url.toString());
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}. ${text}`);
      }

      const data = await response.json();
      setCsvHeaders(data.headers || []);
      setCsvRows(data.rows || []);
      csvCacheRef.current.set(cacheKey, {
        headers: data.headers || [],
        rows: data.rows || [],
      });
    } catch (err) {
      const message = err.details || err.message || "Failed to load CSV preview";
      setCsvError(message);
      setCsvHeaders([]);
      setCsvRows([]);
    } finally {
      setCsvLoading(false);
    }
  };

  const loadRevenueDates = async (type = revenueDatesType) => {
    try {
      const url = new URL(`${environment.apiUrl}/revop/s3-csv-dates`);
      url.searchParams.set("type", type);
      const response = await customFetch(url.toString());
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        return;
      }
      const data = await response.json();
      const dates = Array.isArray(data.dates) ? data.dates : [];
      setRevenueDates(dates);
      if (dates.length && !selectedRevenueDate) setSelectedRevenueDate(dates[0]);
    } catch (err) {
      console.error("Failed to load revenue dates:", err);
      setRevenueDates([]);
    }
  };

  const loadLatestAgingSummary = async () => {
    setAgingSummaryLoading(true);
    try {
      const url = new URL(`${environment.apiUrl}/revop/aging-summary-total`);
      const response = await customFetch(url.toString());
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}. ${text}`);
      }
      const data = await response.json();
      setAgingSummaryTotal(typeof data.total === "number" ? data.total : null);
      setAgingSummaryDate(data.date || "");
      setAgingSummaryPeriod(data.period || "");
    } catch (err) {
      console.error("Failed to load aging summary total:", err);
      setAgingSummaryTotal(null);
      setAgingSummaryDate("");
      setAgingSummaryPeriod("");
    } finally {
      setAgingSummaryLoading(false);
    }
  };

  const loadSalesByCustomerTotal = async (date) => {
    setSalesByCustomerLoading(true);
    try {
      const url = new URL(`${environment.apiUrl}/revop/sales-by-customer-total`);
      if (date) url.searchParams.set("date", date);
      const response = await customFetch(url.toString());
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}. ${text}`);
      }
      const data = await response.json();
      setSalesByCustomerTotal(typeof data.total === "number" ? data.total : null);
      setSalesByCustomerDate(data.date || "");
      setSalesByCustomerPeriod(data.period || "");
    } catch (err) {
      console.error("Failed to load sales by customer total:", err);
      setSalesByCustomerTotal(null);
      setSalesByCustomerDate("");
      setSalesByCustomerPeriod("");
    } finally {
      setSalesByCustomerLoading(false);
    }
  };

  const loadSalesByCustomerPreviewTotal = async (date) => {
    if (!date) {
      setSalesByCustomerPreviewTotal(null);
      setSalesByCustomerPreviewDate("");
      setSalesByCustomerPreviewLoading(false);
      setSalesByCustomerPreviewPeriod("");
      return;
    }
    setSalesByCustomerPreviewLoading(true);
    try {
      const url = new URL(`${environment.apiUrl}/revop/sales-by-customer-total`);
      url.searchParams.set("date", date);
      const response = await customFetch(url.toString());
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}. ${text}`);
      }
      const data = await response.json();
      setSalesByCustomerPreviewTotal(typeof data.total === "number" ? data.total : null);
      setSalesByCustomerPreviewDate(data.date || "");
      setSalesByCustomerPreviewPeriod(data.period || "");
    } catch (err) {
      console.error("Failed to load sales by customer preview total:", err);
      setSalesByCustomerPreviewTotal(null);
      setSalesByCustomerPreviewDate("");
      setSalesByCustomerPreviewPeriod("");
    } finally {
      setSalesByCustomerPreviewLoading(false);
    }
  };

  const loadAgingSummaryCustomers = async (date) => {
    setAgingSummaryCustomersLoading(true);
    try {
      const url = new URL(`${environment.apiUrl}/revop/s3-csv`);
      url.searchParams.set("type", "aging_summary");
      if (date) url.searchParams.set("date", date);
      url.searchParams.set("maxRows", "500");
      const response = await customFetch(url.toString());
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}. ${text}`);
      }
      const data = await response.json();
      const names = extractCustomerNames(data.rows || [], data.headers || []);
      const amounts = extractCustomerAmounts(data.rows || [], data.headers || []);
      setAgingSummaryCustomers(names);
      setAgingSummaryCustomerAmounts(amounts);
    } catch (err) {
      console.error("Failed to load aging summary customers:", err);
      setAgingSummaryCustomers(new Set());
      setAgingSummaryCustomerAmounts(new Map());
    } finally {
      setAgingSummaryCustomersLoading(false);
    }
  };

  const loadAgingSummaryPreviewTotal = async (date) => {
    if (!date) {
      setAgingSummaryPreviewTotal(null);
      setAgingSummaryPreviewDate("");
      setAgingSummaryPreviewPeriod("");
      setAgingSummaryPreviewLoading(false);
      return;
    }
    setAgingSummaryPreviewLoading(true);
    try {
      const url = new URL(`${environment.apiUrl}/revop/aging-summary-total`);
      url.searchParams.set("date", date);
      const response = await customFetch(url.toString());
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}. ${text}`);
      }
      const data = await response.json();
      setAgingSummaryPreviewTotal(typeof data.total === "number" ? data.total : null);
      setAgingSummaryPreviewDate(data.date || date);
      setAgingSummaryPreviewPeriod(data.period || "");
    } catch (err) {
      console.error("Failed to load aging summary preview total:", err);
      setAgingSummaryPreviewTotal(null);
      setAgingSummaryPreviewDate(date || "");
      setAgingSummaryPreviewPeriod("");
    } finally {
      setAgingSummaryPreviewLoading(false);
    }
  };

  const loadLatestSalesByMonth = async () => {
    if (salesByMonthInitRef.current) return;
    salesByMonthInitRef.current = true;
    setSalesByMonthCardLoading(true);
    try {
      const series = await fetchSalesByMonthSeries();
      setSalesByMonthCardSeries(Array.isArray(series) ? series : []);
    } catch (err) {
      console.error("Failed to load latest sales by month:", err);
      setSalesByMonthCardSeries([]);
    } finally {
      setSalesByMonthCardLoading(false);
    }
  };

  const loadSalesByMonthSummary = async (date) => {
    const cacheKey = date ? `date:${date}` : "latest";
    const cached = salesByMonthSummaryCacheRef.current.get(cacheKey);
    if (cached) {
      setSalesByMonthSeriesState(cached);
      setSalesByMonthSeriesLoading(false);
      return;
    }
    setSalesByMonthSeriesLoading(true);
    try {
      const series = await fetchSalesByMonthSeries(date);
      salesByMonthSummaryCacheRef.current.set(cacheKey, series);
      setSalesByMonthSeriesState(series);
    } catch (err) {
      console.error("Failed to load sales by month summary:", err);
      setSalesByMonthSeriesState([]);
    } finally {
      setSalesByMonthSeriesLoading(false);
    }
  };

  useEffect(() => {
    loadLatestAgingSummary();
    loadSalesByCustomerTotal();
    loadLatestSalesByMonth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
      <div
        className="min-h-screen bg-slate-50 text-slate-900 pb-10 overflow-x-hidden"
        style={{
          marginLeft: "var(--sidebar-width, 250px)",
          width: "calc(100% - var(--sidebar-width, 250px))",
        }}
      >
        <div className="w-full max-w-[1600px] px-4 lg:px-6 mx-auto pt-4 md:pt-6 lg:pt-10 xl:pt-12 pb-8 space-y-6">
          <DashboardHeader
            title={`Welcome back, ${username}`}
            date={todaysDate}
            showQuickLinks={showQuickLinks}
            onToggleQuickLinks={() => setShowQuickLinks((s) => !s)}
          />

          {/* At a Glance */}
          <div className="space-y-4">
            <SectionHeader title="At a Glance" size="text-[20px] mt-3"/>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 px-12">
              <ActiveDoctorsCard
                value={currentMonthActiveDoctors}
                superOrgsCount={activeSuperOrgs.value}
                loading={currentMonthActiveDoctors === null || activeSuperOrgs.loading}
                onOpenGraph={() => setActivityGraphOpen(true)}
                tooltip="Active doctors this month; matches the chart below. Super orgs count shown below."
              />
              <NewUsersCard
                value={newUsers.loading ? "…" : formatNumber(newUsers.value)}
                loading={newUsers.loading || newUsersDetailsLoading}
                details={newUsersDetails}
                isOpen={newUsersOpen}
                onToggle={toggleNewUsers}
                onClose={() => setNewUsersOpen(false)}
                tooltip="Newly activated doctors this month."
              />
              <SmallMetricCard
                icon={CheckCheck}
                iconSize={18}
                label="Collected Revenue"
                labelClassName="flex justify-center gap-3 text-[13px] font-medium text-slate-700 uppercase tracking-wide"
                iconClassName="text-[18px] text-slate-500 mb-2"
                value={
                  salesByCustomerLoading || agingSummaryLoading
                    ? "…"
                    : formatCurrency(collectedRevenueTotal)
                }
                valueClassName="text-2xl font-semibold text-ktx-purple underline underline-offset-4 tabular-nums leading-none mt-4"
                subLabel={
                  salesByCustomerPeriod
                    ? salesByCustomerPeriod
                    : salesByCustomerDate
                      ? `As of ${formatDateLabel(salesByCustomerDate)}`
                      : "Latest sales by customer"
                }
                subLabelClassName="text-[11px] text-slate-500 uppercase tracking-wide text-center mt-2"
                onClick={() => openRevenueDates("Collected Revenue Dates", "sales_by_customer")}
                tooltip="Most recent sales by customer total minus pipeline revenue."
              />
              <SmallMetricCard
                icon={Hospital}
                iconSize={18}
                label="Active Orgs"
                labelClassName="flex justify-center gap-3.5 text-[13px] font-medium text-slate-700 uppercase tracking-wide"
                value={activeSuperOrgs.loading ? "…" : formatNumber(activeSuperOrgs.value)}
                valueClassName="inline-block text-2xl font-semibold text-ktx-purple underline underline-offset-4 tabular-nums leading-none mt-4"
                subLabel="this month"
                subLabelClassName="text-[11px] text-slate-500 uppercase tracking-wide text-center mt-2"
                onClick={openBillableOrgs}
                tooltip="Active organizations this month."
              />
              <SmallMetricCard
                icon={Mic}
                iconSize={18}
                cardClassName="hover:border-ktx-purple/40 hover:shadow-md hover:bg-ktx-purple/5"
                label="Active Usage"
                labelClassName="flex justify-center gap-3.5 text-[13px] font-medium text-slate-700 uppercase tracking-wide"
                value={patients.loading ? "…" : formatNumber(patients.value)}
                valueClassName="text-2xl font-semibold text-ktx-purple underline underline-offset-4 tabular-nums leading-none mt-4"
                subLabel="this Month"
                subLabelClassName="text-[11px] text-slate-500 uppercase tracking-wide text-center mt-2"
                onClick={openActiveUsage}
                tooltip="Patients seen this month."
              />
              <SmallMetricCard
                icon={TrendingUp}
                iconSize={18}
                label="Pipeline Revenue"
                labelClassName="flex justify-center gap-3.5 text-[13px] font-medium text-slate-700 uppercase tracking-wide"
                iconClassName="text-[18px] text-slate-500 mb-2"
                value={agingSummaryLoading ? "…" : formatCurrency(agingSummaryTotal)}
                valueClassName="text-2xl font-semibold text-ktx-purple underline underline-offset-4 tabular-nums leading-none mt-4"
                subLabel={
                  agingSummaryDate
                    ? `As of ${formatDateLabel(agingSummaryDate)}`
                    : agingSummaryPeriod || "Latest aging summary"
                }
                subLabelClassName="text-[11px] text-slate-500 uppercase tracking-wide text-center mt-2"
                onClick={() => openRevenueDates("Pipeline Revenue Dates", "aging_summary")}
                tooltip="Latest aging summary total."
              />
            </div>
          </div>

          {openModal === "Processing" && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-6">
              <div
                className="min-w-[320px] rounded-xl border border-slate-200 bg-white shadow-xl"
                style={{ resize: "both", overflow: "auto", width: "min(90vw, 32rem)" }}
              >
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Processing Notes</div>
                    <div className="text-xs text-slate-500">
                      {processingHelperData === null
                        ? "Loading processing notes..."
                        : `${processingHelperData.length} notes in progress`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpenModal("")}
                    className="text-slate-400 hover:text-slate-600"
                    aria-label="Close processing notes"
                  >
                    ✕
                  </button>
                </div>
                <div className="max-h-[60vh] overflow-auto p-4 text-xs text-slate-600">
                  {processingHelperData === null ? (
                    <div className="flex items-center gap-2 text-slate-400">
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-slate-500" />
                      Loading processing notes...
                    </div>
                  ) : processingHelperData.length ? (
                    <div className="space-y-3">
                      {processingHelperData.map((note) => (
                        <div key={note.note_id || note.patient_id} className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-slate-800 font-medium">
                              {note.user_name || "Unknown doctor"}
                            </div>
                            <div className="truncate text-slate-500">
                              {note.user_email || "No email"}
                            </div>
                          </div>
                          <div className="text-right whitespace-nowrap text-slate-400">
                            {note.job ? "Job active" : "No job"}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-slate-400">No processing notes found.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {billableOrgsOpen && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-6">
              <div
                className="min-w-[320px] rounded-xl border border-slate-200 bg-white shadow-xl"
                style={{ resize: "both", overflow: "auto", width: "min(90vw, 32rem)" }}
              >
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Active Orgs</div>
                    <div className="text-xs text-slate-500">
                      {billableOrgsLoading
                        ? "Loading Active orgs..."
                        : `${billableOrgsList.length} orgs this month`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setBillableOrgsOpen(false)}
                    className="text-slate-400 hover:text-slate-600"
                    aria-label="Close billable orgs"
                  >
                    ✕
                  </button>
                </div>
                <div className="max-h-[75vh] overflow-auto p-4 text-sm text-slate-600">
                  {billableOrgsLoading ? (
                    <div className="flex items-center gap-2 text-slate-400">
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-slate-500" />
                      Loading billable orgs...
                    </div>
                  ) : billableOrgsList.length ? (
                    <div className="space-y-2">
                      {billableOrgsList.map((org) => (
                        <button
                          key={org.name}
                          type="button"
                          onClick={() => openBillableOrgDetail(org.name)}
                          className="w-full flex items-center justify-between rounded-lg border border-slate-200/80 bg-slate-50 px-3 py-2 text-slate-700 transition-colors hover:bg-ktx-purple/5 hover:border-ktx-purple/30"
                          aria-label={`Open ${org.name} details`}
                        >
                          <span className="font-medium">{org.name}</span>
                          <span className="text-xs text-slate-500 tabular-nums">
                            {formatNumber(org.active_doctors)} active doctors
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-slate-500">No billable orgs found for this month.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeUsageOpen && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-6">
              <div
                className="min-w-[320px] rounded-xl border border-slate-200 bg-white shadow-xl"
                style={{ resize: "both", overflow: "auto", width: "min(90vw, 48rem)" }}
              >
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Active Usage</div>
                    <div className="text-xs text-slate-500">
                      {activeUsageLoading
                        ? "Loading active usage..."
                        : `${activeUsageList.length} doctors this month`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveUsageOpen(false)}
                    className="text-slate-400 hover:text-slate-600"
                    aria-label="Close active usage"
                  >
                    ✕
                  </button>
                </div>
                <div className="max-h-[75vh] overflow-auto p-4 text-sm text-slate-600">
                  {activeUsageLoading ? (
                    <div className="flex items-center gap-2 text-slate-400">
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-slate-500" />
                      Loading active usage...
                    </div>
                  ) : activeUsageList.length ? (
                    <div className="overflow-x-auto border border-slate-200 rounded-lg">
                      <table className="min-w-full text-sm text-slate-700">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                              Doctor
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                              Org
                            </th>
                            <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                              Recordings
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeUsageList.map((row, index) => (
                            <tr
                              key={`${row.doctor_name || "doctor"}-${row.org || "org"}-${index}`}
                              className={index % 2 === 0 ? "bg-white" : "bg-slate-50/50"}
                            >
                              <td className="px-3 py-2 border-b border-slate-100">
                                {row.doctor_name || "—"}
                              </td>
                              <td className="px-3 py-2 border-b border-slate-100">
                                {row.org || "—"}
                              </td>
                              <td className="px-3 py-2 border-b border-slate-100 text-right tabular-nums">
                                {formatNumber(row.recordings)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-slate-500">No active usage found for this month.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {revenueDatesOpen && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-6">
              <div
                className="min-w-[320px] rounded-xl border border-slate-200 bg-white shadow-xl"
                style={{ resize: "both", overflow: "auto", width: "min(90vw, 32rem)" }}
              >
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{revenueDatesTitle}</div>
                    <div className="text-xs text-slate-500">Select a month to update the revenue card</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRevenueDatesOpen(false)}
                    className="text-slate-400 hover:text-slate-600"
                    aria-label="Close revenue dates"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {revenueDates.length === 0 && (
                      <div className="text-xs text-slate-400">No dates found.</div>
                    )}
                    {revenueDates.map((date) => (
                      <button
                        key={date}
                        type="button"
                        onClick={() => {
                          setSelectedRevenueDate(date);
                          setCsvType(revenueDatesType);
                          setCsvDate(date);
                          setCsvKey("");
                          loadCsvPreview("", revenueDatesType, date);
                          if (revenueDatesType === "sales_by_customer") {
                            loadSalesByCustomerPreviewTotal(date);
                            loadAgingSummaryPreviewTotal(date);
                            loadAgingSummaryCustomers(date);
                          } else {
                            setSalesByCustomerPreviewTotal(null);
                            setSalesByCustomerPreviewDate("");
                            setAgingSummaryPreviewTotal(null);
                            setAgingSummaryPreviewDate("");
                            setAgingSummaryPreviewPeriod("");
                            setAgingSummaryCustomers(new Set());
                            setAgingSummaryCustomerAmounts(new Map());
                          }
                          setRevenueDatesOpen(false);
                          openCsvPreview();
                        }}
                        className={`rounded-full px-3 py-1 text-xs uppercase tracking-wide transition ${
                          selectedRevenueDate === date
                            ? "bg-ktx-purple text-white"
                            : "bg-slate-100 text-slate-600 hover:text-slate-800"
                        }`}
                      >
                        {formatDateLabel(date)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {csvPreviewOpen && csvType !== "sales_by_month" && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-6">
              <div
                className="min-w-[320px] rounded-xl border border-slate-200 bg-white shadow-xl"
                style={{ resize: "both", overflow: "auto", width: "min(90vw, 72rem)" }}
              >
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">QuickBook Report</div>
                    <div className="text-xs text-slate-500">
                      {selectedRevenueDate ? formatDateLabel(selectedRevenueDate) : "Select a date"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCsvPreviewOpen(false)}
                    className="text-slate-400 hover:text-slate-600"
                    aria-label="Close CSV preview"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-4">
                  {(() => {
                    const salesByCustomerMeta =
                      csvType === "sales_by_customer"
                        ? findSalesByCustomerTableMeta(csvRows, csvHeaders)
                        : null;
                    const amountColumnIndex =
                      csvType === "sales_by_customer"
                        ? salesByCustomerMeta?.amountColumnIndex ?? -1
                        : getAmountColumnIndex(csvHeaders);
                    const dataStartIndex =
                      csvType === "sales_by_customer" ? salesByCustomerMeta?.dataStartIndex ?? 0 : 0;

                    return (
                      <>
                  {csvError && (
                    <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 mb-4">
                      {csvError}
                    </div>
                  )}
                  {csvType === "sales_by_customer" && (
                    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Collected Revenue Calculation
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        Sales by customer total ({salesByCustomerDisplayDate ? formatDateLabel(salesByCustomerDisplayDate) : "latest"}{salesByCustomerDisplayPeriod ? ` • ${salesByCustomerDisplayPeriod}` : ""}) minus pipeline revenue ({agingSummaryDisplayDate ? formatDateLabel(agingSummaryDisplayDate) : "latest"}{agingSummaryDisplayPeriod ? ` • ${agingSummaryDisplayPeriod}` : ""}).
                      </div>
                      <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                        <div className="flex items-center gap-2">
                          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-rose-200" />
                          Excluded (also in aging summary)
                        </div>
                        {agingSummaryCustomersLoading && <div>Loading aging summary customers…</div>}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-700">
                        <div>
                          <span className="text-slate-500">Sales by customer:</span>{" "}
                          <span className="font-semibold text-slate-900">
                            {salesByCustomerPreviewLoading
                              ? "…"
                              : formatCurrency(salesByCustomerDisplayTotal)}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500">Pipeline revenue:</span>{" "}
                          <span className="font-semibold text-slate-900">
                            {agingSummaryPreviewLoading || agingSummaryLoading
                              ? "…"
                              : formatCurrency(agingSummaryDisplayTotal)}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500">Collected revenue:</span>{" "}
                          <span className="font-semibold text-ktx-purple">
                            {salesByCustomerPreviewLoading || agingSummaryPreviewLoading || agingSummaryLoading
                              ? "…"
                              : formatCurrency(collectedRevenueDisplayTotal)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-[75vh]">
                    <table className="min-w-full text-sm text-slate-700">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          {csvHeaders.map((header, index) => (
                            <th
                              key={`${header}-${index}`}
                              className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200"
                            >
                              {header || `Column ${index + 1}`}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={Math.max(csvHeaders.length, 1)}
                              className="px-3 py-6 text-center text-slate-400"
                            >
                              {csvLoading ? "Loading..." : "No data found."}
                            </td>
                          </tr>
                        ) : (
                          csvRows.map((row, rowIndex) => {
                            const firstCell = row?.[0]?.toString?.().toLowerCase() || "";
                            const normalizedName = normalizeName(row?.[0]);
                            const amountValue = amountColumnIndex >= 0 ? parseMoney(row?.[amountColumnIndex]) : null;
                            const hideZeroAmountRow =
                              csvType === "sales_by_customer" &&
                              rowIndex >= dataStartIndex &&
                              amountColumnIndex >= 0 &&
                              amountValue === 0;
                            const isExcluded =
                              csvType === "sales_by_customer" &&
                              rowIndex >= dataStartIndex &&
                              amountColumnIndex >= 0 &&
                              amountValue !== null &&
                              amountValue > 0 &&
                              !firstCell.includes("total") &&
                              !firstCell.includes("subtotal") &&
                              agingSummaryCustomers.has(normalizedName);

                            if (hideZeroAmountRow) return null;

                            return (
                              <tr
                                key={`row-${rowIndex}`}
                                className={`border-b border-slate-100 ${isExcluded ? "bg-rose-50/40" : ""}`}
                              >
                                {row.map((cell, cellIndex) => (
                                  <td
                                    key={`cell-${rowIndex}-${cellIndex}`}
                                    className={`px-3 py-2 ${
                                      isExcluded && cellIndex === amountColumnIndex
                                        ? "font-semibold text-rose-700"
                                        : ""
                                    }`}
                                  >
                                    {isExcluded && cellIndex === amountColumnIndex ? (
                                      <span className="inline-flex items-center gap-2">
                                        <span>{cell}</span>
                                        {agingSummaryCustomerAmounts.get(normalizedName) && (
                                          <span className="text-xs font-medium text-slate-500">
                                            ({agingSummaryCustomerAmounts.get(normalizedName)} in aging summary)
                                          </span>
                                        )}
                                      </span>
                                    ) : (
                                      cell
                                    )}
                                  </td>
                                ))}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-10 px-12">
            <Card
              className="group border border-slate-200/80 bg-white shadow-sm rounded-xl h-full transition-shadow transition-color cursor-pointer hover:border-ktx-purple/20 hover:bg-ktx-purple/5"
              role="button"
              tabIndex={0}
              title="Trend of active vs total doctors over time."
              onClick={() => setDoctorAdoptionOpen(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setDoctorAdoptionOpen(true);
                }
              }}
              aria-label="Expand doctor adoption"
            >
                <div className="flex items-center justify-between px-6 pt-6 pb-2">
                  <CardDescription className="text-[13px] flex justify-center uppercase tracking-wide text-slate-700">
                    <span className="flex items-center gap-3 mb-5">
                      <UserStar size={20} className="text-[20px] text-slate-500" />
                      Doctor Adoption
                    </span>
                  </CardDescription>
              </div>
              <CardContent className="space-y-4">
                {dailyActivations && activeVsTotal ? (
                  <>
                    <div className="flex items-center justify-between text-xs uppercase tracking-wide text-slate-500">
                      <RevOpsActivityStats data={activeVsTotal} />
                    </div>
                    <div className="flex-1 min-h-[280px] mt-1 cursor-pointer [&_.recharts-wrapper]:cursor-pointer [&_.recharts-surface]:cursor-pointer [&_svg]:cursor-pointer [&_.recharts-legend-wrapper]:cursor-pointer [&_.recharts-legend-item]:cursor-pointer">
                      <RevOpsLineGraph
                        data={dailyActivations}
                        totalsData={activeVsTotal}
                        showActivity={false}
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex min-h-[280px] items-center justify-center mt-7">
                    <span className="inline-block h-16 w-16 animate-spin rounded-full border-4 border-ktx-purple/30 border-t-ktx-purple" />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card
              className="group border border-slate-200/80 bg-white shadow-sm rounded-xl h-full transition-shadow transition-color cursor-pointer hover:border-ktx-purple/40 hover:shadow-md hover:bg-ktx-purple/5"
              role="button"
              tabIndex={0}
              title="Monthly split of revenue."
              onClick={() => {
                setRevenueDatesType("sales_by_month");
                loadRevenueDates("sales_by_month");
                setCsvPreviewOpen(false);
                setShowRevenueBreakdownTable(false);
                setRevenueBreakdownDateSelected(false);
                setRevenueBreakdownOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setRevenueDatesType("sales_by_month");
                  loadRevenueDates("sales_by_month");
                  setCsvPreviewOpen(false);
                  setShowRevenueBreakdownTable(false);
                  setRevenueBreakdownDateSelected(false);
                  setRevenueBreakdownOpen(true);
                }
              }}
              aria-label="Expand revenue breakdown"
            >
                <div className="flex items-center justify-between px-6 pt-6 pb-2">
                  <div className="flex items-center gap-3">
                    <CardDescription className="text-[13px] uppercase tracking-wide text-slate-700">
                    <span className="flex items-center gap-3 mb-5">
                      <CalendarSearch size={20} className="text-[20px] text-slate-500" />
                      Revenue Breakdown
                    </span>
                    </CardDescription>
                  </div>
              </div>
              <CardContent className="space-y-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
                      <span className="text-sm text-gray-600">Cumulative Total:</span>
                      <span className="text-sm font-semibold text-gray-700">
                        {revenueBreakdownCardCumulative === null
                          ? "—"
                          : formatCurrency(revenueBreakdownCardCumulative)}
                      </span>
                    </div>
                    <div className="flex-1 min-h-[280px] mt-1 cursor-pointer [&_.recharts-wrapper]:cursor-pointer [&_.recharts-surface]:cursor-pointer [&_svg]:cursor-pointer [&_.recharts-legend-wrapper]:cursor-pointer [&_.recharts-legend-item]:cursor-pointer flex items-center justify-center">
                      {salesByMonthCardLoading ? (
                        <span className="inline-block h-16 w-16 animate-spin rounded-full border-4 border-ktx-purple/30 border-t-ktx-purple" />
                      ) : salesByMonthCardSeries.length ? (
                        <div className="w-full">
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart
                              data={salesByMonthCardSeries}
                              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                            >
                              <defs>
                                <linearGradient id="revopsRevenueGradientCard" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#f6cf98" />
                                  <stop offset="55%" stopColor="#f1ab72" />
                                  <stop offset="100%" stopColor="#e1835c" />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis dataKey="name" tickLine={false} axisLine={false} fontSize={11} color="#e0e0e0" />
                              <YAxis tickLine={false} axisLine={false} fontSize={11} color="#e0e0e0"/>
                              <Tooltip
                                formatter={(value) => formatCurrency(value)}
                                labelStyle={{ fontSize: 11, textTransform: "uppercase" }}
                                cursor={{ fill: "rgba(15, 23, 42, 0.06)" }}
                              />
                              <Legend height={24} iconType="square" wrapperStyle={{ fontSize: 15, marginBottom: -6 }} />
                              <Bar
                                dataKey="total"
                                fill="url(#revopsRevenueGradientCard)"
                                fillOpacity={0.68}
                                barSize={28}
                                name="Total"
                                radius={[0, 0, 0, 0]}
                                shape={<SlantedBar />}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="text-sm text-slate-400">No sales by month data found.</div>
                      )}
                    </div>
              </CardContent>
            </Card>
          </div>

          {activityGraphOpen && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-6">
              <div
                className="min-w-[320px] max-h-[85vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl"
                style={{ resize: "both", overflow: "auto", width: "min(90vw, 56rem)" }}
              >
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Current Month Activity</div>
                    <div className="text-xs text-slate-500">Daily Activation • Weekly Avg</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActivityGraphOpen(false)}
                    className="text-slate-400 hover:text-slate-600"
                    aria-label="Close activity graph"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-4">
                  <RevOpsLineGraph
                    data={dailyActivations}
                    totalsData={activeVsTotal}
                    showTotals={false}
                    showActivity
                  />
                </div>
              </div>
            </div>
          )}

          {doctorAdoptionOpen && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-6">
              <div
                className="min-w-[320px] max-h-[85vh] flex flex-col rounded-xl border border-slate-200 bg-white shadow-xl"
                style={{ resize: "both", overflow: "auto", width: "min(90vw, 56rem)", maxHeight: "85vh" }}
              >
                <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Doctor Adoption</div>
                    <div className="text-xs text-slate-500">Monthly Active • Total Doctors</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="inline-flex items-center rounded-full bg-slate-100 p-1 text-[11px] uppercase tracking-wide text-slate-500">
                      {[
                        { value: "3m", label: "3M" },
                        { value: "6m", label: "6M" },
                        { value: "12m", label: "12M" },
                        { value: "all", label: "All" },
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setDoctorAdoptionRange(option.value)}
                          className={`px-2.5 py-1 rounded-full transition ${
                            doctorAdoptionRange === option.value
                              ? "bg-white text-slate-900 shadow-sm"
                              : "text-slate-500 hover:text-slate-700"
                          }`}
                          aria-pressed={doctorAdoptionRange === option.value}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setDoctorAdoptionOpen(false);
                        setSelectedAdoptionMonth(null);
                        setAdoptionMonthDoctors([]);
                        setAdoptionMonthDoctorsTotal(0);
                      }}
                      className="text-slate-400 hover:text-slate-600"
                      aria-label="Close doctor adoption"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-auto p-4">
                  <RevOpsLineGraph
                    data={dailyActivations}
                    totalsData={filteredAdoptionTotals}
                    showActivity={false}
                  />

                  <div className="mt-6 border-t border-slate-200 pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Select a month to see the doctors
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setAdoptionExportError("");
                            setAdoptionExportResultCount(null);
                            setAdoptionExportOpen(true);
                          }}
                          className="text-xs font-medium text-ktx-purple hover:text-ktx-purple/80"
                        >
                          Export month range…
                        </button>
                        {selectedAdoptionMonth && (
                          <button
                            type="button"
                            onClick={() => setSelectedAdoptionMonth(null)}
                            className="text-xs font-medium text-slate-400 hover:text-slate-600"
                          >
                            Clear selection
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {Array.isArray(filteredAdoptionTotals) && filteredAdoptionTotals.length ? (
                        [...filteredAdoptionTotals].reverse().map((entry) => (
                          <button
                            key={entry.name}
                            type="button"
                            onClick={() => selectAdoptionMonth(entry.name)}
                            className={`rounded-full px-3 py-1 text-xs uppercase tracking-wide transition ${
                              selectedAdoptionMonth === entry.name
                                ? "bg-ktx-purple text-white"
                                : "bg-slate-100 text-slate-600 hover:text-slate-800"
                            }`}
                            aria-pressed={selectedAdoptionMonth === entry.name}
                          >
                            {formatMonthLabel(entry.name)}
                          </button>
                        ))
                      ) : (
                        <div className="text-xs text-slate-400">No months available for this range.</div>
                      )}
                    </div>

                    {selectedAdoptionMonth && (
                      <>
                        <div className="mt-4 flex items-center justify-between gap-2">
                          <div className="text-xs text-slate-500">
                            {adoptionMonthDoctorsLoading
                              ? "Loading..."
                              : `${adoptionMonthDoctorsTotal} doctor${adoptionMonthDoctorsTotal === 1 ? "" : "s"} active in ${formatMonthLabel(selectedAdoptionMonth)}`}
                          </div>
                          <button
                            type="button"
                            onClick={downloadAdoptionMonthXlsx}
                            disabled={adoptionMonthDoctorsLoading || !adoptionMonthDoctors.length}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600 shadow-sm transition hover:border-ktx-purple/40 hover:text-ktx-purple disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-600"
                          >
                            Download Full List (XLSX)
                          </button>
                        </div>

                        <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <AdoptionDoctorsMiniTable
                            title="Top Users"
                            doctors={topAdoptionMonthDoctors}
                            loading={adoptionMonthDoctorsLoading}
                            emptyLabel="No active doctors found for the selected month."
                          />
                          <AdoptionDoctorsMiniTable
                            title="Lower Users"
                            doctors={bottomAdoptionMonthDoctors}
                            loading={adoptionMonthDoctorsLoading}
                            emptyLabel={
                              adoptionMonthDoctorsLoading || adoptionMonthDoctors.length > ADOPTION_TOP_BOTTOM_COUNT
                                ? "No active doctors found for the selected month."
                                : "Fewer than 10 doctors total — see Top Users."
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {adoptionExportOpen && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 p-6">
              <div className="min-w-[320px] w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Export Doctor List</div>
                    <div className="text-xs text-slate-500">Pick a month range, download one Excel file with a tab per month</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAdoptionExportOpen(false)}
                    className="text-slate-400 hover:text-slate-600"
                    aria-label="Close export doctor list"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-4 space-y-4">
                  {adoptionAllMonths === null ? (
                    <div className="text-xs text-slate-400">Loading available months...</div>
                  ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">From</span>
                      <select
                        value={adoptionExportFromMonth}
                        onChange={(event) => setAdoptionExportFromMonth(event.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-ktx-purple/40 focus:outline-none"
                      >
                        {adoptionExportMonthOptions.map((month) => (
                          <option key={month} value={month}>
                            {formatMonthLabel(month)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">To</span>
                      <select
                        value={adoptionExportToMonth}
                        onChange={(event) => setAdoptionExportToMonth(event.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-ktx-purple/40 focus:outline-none"
                      >
                        {adoptionExportMonthOptions.map((month) => (
                          <option key={month} value={month}>
                            {formatMonthLabel(month)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  )}

                  {adoptionExportError && (
                    <div className="text-xs text-rose-600">{adoptionExportError}</div>
                  )}
                  {adoptionExportResultCount !== null && !adoptionExportError && (
                    <div className="text-xs text-emerald-600">
                      Downloaded {adoptionExportResultCount} doctor{adoptionExportResultCount === 1 ? "" : "s"} across{" "}
                      {getMonthsInRange(adoptionExportFromMonth, adoptionExportToMonth).length} month(s).
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={downloadAdoptionRangeXlsx}
                    disabled={adoptionExportLoading || !adoptionExportMonthOptions.length}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-ktx-purple px-4 py-2 text-sm font-semibold text-white transition hover:bg-ktx-purple/90 disabled:opacity-40"
                  >
                    {adoptionExportLoading ? "Generating…" : "Download XLSX"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {revenueBreakdownOpen && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-6">
              <div
                className="min-w-[320px] h-[560px] max-h-[80vh] rounded-xl border border-slate-200 bg-white shadow-xl flex flex-col"
                style={{ resize: "both", overflow: "auto", width: "min(90vw, 56rem)" }}
              >
                <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Revenue Breakdown</div>
                    <div className="text-xs text-slate-500">Pipeline • ARR • Collected</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setRevenueBreakdownOpen(false);
                        setShowRevenueBreakdownTable(false);
                        setRevenueBreakdownDateSelected(false);
                      }}
                      className="text-slate-400 hover:text-slate-600"
                      aria-label="Close revenue breakdown"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                <div className="px-4 pt-4">
                  <div className="flex flex-wrap gap-2">
                    {revenueDates.length === 0 && (
                      <div className="text-xs text-slate-400">No dates found.</div>
                    )}
                    {revenueDates.map((date) => (
                      <button
                        key={date}
                        type="button"
                        onClick={() => {
                          setSelectedRevenueDate(date);
                          setCsvType(revenueDatesType);
                          setCsvDate(date);
                          setCsvKey("");
                          loadSalesByMonthSummary(date);
                          setRevenueBreakdownDateSelected(true);
                        }}
                        className={`rounded-full px-3 py-1 text-xs uppercase tracking-wide transition ${
                          selectedRevenueDate === date
                            ? "bg-ktx-purple text-white"
                            : "bg-slate-100 text-slate-600 hover:text-slate-800"
                        }`}
                      >
                        {formatDateLabel(date)}
                      </button>
                    ))}
                  </div>
                  {revenueBreakdownDateSelected && revenueDatesType === "sales_by_month" && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          if (!showRevenueBreakdownTable) {
                            loadCsvPreview("", revenueDatesType, selectedRevenueDate);
                          }
                          setShowRevenueBreakdownTable((prev) => !prev);
                        }}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600 shadow-sm transition hover:border-ktx-purple/40 hover:text-ktx-purple"
                      >
                        {showRevenueBreakdownTable ? "Hide Table Details" : "View Table Details"}
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  {revenueBreakdownDateSelected ? (
                    salesByMonthSeriesLoading ? (
                      <div className="flex h-[360px] items-center justify-center text-sm text-slate-400">
                        Loading sales by month...
                      </div>
                    ) : salesByMonthSeriesState.length ? (
                      <>
                        <ResponsiveContainer width="100%" height={360}>
                          <BarChart
                            data={salesByMonthSeriesState}
                            margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                          >
                            <defs>
                              <linearGradient id="revopsRevenueGradientModal" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#f8c46f" />
                                <stop offset="55%" stopColor="#f28c3c" />
                                <stop offset="100%" stopColor="#e45b2e" />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis dataKey="name" tickLine={false} axisLine={false} fontSize={11} color="#e0e0e0" />
                            <YAxis tickLine={false} axisLine={false} fontSize={11} color="#e0e0e0" />
                            <Tooltip
                              formatter={(value) => formatCurrency(value)}
                              labelStyle={{ fontSize: 11, textTransform: "uppercase" }}
                              cursor={{ fill: "rgba(15, 23, 42, 0.06)" }}
                            />
                            <Legend height={24} iconType="square" wrapperStyle={{ fontSize: 15, marginBottom: -15}} />
                            <Bar
                              dataKey="total"
                              fill="url(#revopsRevenueGradientModal)"
                              fillOpacity={0.95}
                              barSize={30}
                              name="Total"
                              radius={[0, 0, 0, 0]}
                              shape={<SlantedBar />}
                              isAnimationActive={false}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                        <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Cumulative Total:{" "}
                          <span className="text-slate-900">
                            {revenueBreakdownCumulative === null ? "—" : formatCurrency(revenueBreakdownCumulative)}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="flex h-[360px] items-center justify-center text-sm text-slate-400">
                        No sales by month data found for this date.
                      </div>
                    )
                  ) : (
                    <div className="flex h-[360px] items-center justify-center text-sm text-slate-400">
                      Select a report date to view the revenue breakdown.
                    </div>
                  )}
                </div>
                {showRevenueBreakdownTable && revenueDatesType === "sales_by_month" && (
                  <div className="px-4 pb-4">
                    <div className="overflow-x-auto border border-slate-200 rounded-lg">
                      <table className="min-w-full text-sm text-slate-700">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            {csvHeaders.map((header, index) => (
                              <th
                                key={`${header}-${index}`}
                                className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200"
                              >
                                {header || `Column ${index + 1}`}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {csvRows.length === 0 ? (
                            <tr>
                              <td
                                colSpan={Math.max(csvHeaders.length, 1)}
                                className="px-3 py-6 text-center text-slate-400"
                              >
                                {csvLoading ? "Loading..." : "No data found."}
                              </td>
                            </tr>
                          ) : (
                            csvRows.map((row, rowIndex) => (
                              <tr key={`revenue-row-${rowIndex}`} className="border-b border-slate-100">
                                {row.map((cell, cellIndex) => (
                                  <td key={`revenue-cell-${rowIndex}-${cellIndex}`} className="px-3 py-2">
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          )}


          {/* Specialties */}
          <div className="space-y-4 pb-10">
            <div className="gap-4">
              <SectionHeader title="Specialties" size="text-[20px] mt-3"/>
                <CardContent className="p-6 px-12">
                  <div className="bg-white rounded-xl border border-slate-200/80 px-8 py-5 hover:border-ktx-purple/40 hover:bg-ktx-purple/5 hover:shadow-md transition-shadow transition-colors">
                    <div className="min-h-[240px]">
                    <RevOpsPie
                      asyncFunction={getAllRevOpsSpecialties}
                        footerText="15 total"
                        dropdownLabelText="15 total"
                        messageClassName="mt-10"
                        percentageClassName="text-slate-600"
                        legendDropdownClassName="bg-emerald-50 border-emerald-100"
                        onItemSelect={(name) => specialtySearchRef.current?.submitSelection(name)}
                        colors={[
                          "#0f766e",
                          "#11999e",
                          "#22b3b0",
                          "#4ecdc4",
                          "#76e4d6",
                          "#a3f3e3",
                          "#d3fbf1",
                          "#0b5f56",
                        ]}
                      rightColumn={
                        <div className="min-h-[240px] self-start min-w-0 flex items-center justify-center">
                          <RevOpsSpecialtyDoctorSearchbar
                            ref={specialtySearchRef}
                            asyncFunction={getAllRevOpsSpecialties}
                            fetchDoctors={queryDoctorsByRevOpsSpecialty}
                          />
                        </div>
                      }
                    />
                    </div>
                  </div>
                </CardContent>
            </div>
          </div>

          {/* Customers */}
          <div className="space-y-4 pb-10">
            <div className="gap-4">
              <SectionHeader title="Customers" size="text-[20px] mt-3"/>
                <CardContent className="p-6 px-12">
                  <div className="bg-white rounded-xl border border-slate-200/80 px-8 py-5 hover:border-ktx-purple/40 hover:bg-ktx-purple/5 hover:shadow-md transition-shadow transition-colors">
                    <div className="min-h-[240px]">
                    <RevOpsPie
                      asyncFunction={getAllRevOpsCustomersLegend}
                      visibleCount={5}
                      footerText="8 total"
                      dropdownLabelText="8 total"
                      messageClassName="mt-10"
                      percentageClassName="text-slate-600"
                      legendDropdownClassName="bg-sky-50 border-sky-100"
                      showUnpriced
                      onItemSelect={(name) => customerSearchRef.current?.submitSelection(name)}
                      colors={[
                        "#2563eb",
                        "#3b82f6",
                        "#60a5fa",
                        "#7cc4ff",
                        "#9fd6ff",
                        "#c7e6ff",
                        "#1d4ed8",
                        "#0f2a6d",
                      ]}
                      rightColumn={
                        <div className="min-h-[240px] self-start min-w-0 flex items-center justify-center">
                          <RevOpsCustomerDoctorSearchbar
                            ref={customerSearchRef}
                            asyncFunction={getAllRevOpsCustomersLegend}
                            fetchDoctors={getRevOpsSuperOrgDoctors}
                          />
                        </div>
                      }
                      />
                    </div>
                  </div>
                </CardContent>
            </div>
          </div>

          
        </div>
      </div>
    </div>
  );
}
